import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'

import styles from './Text.module.scss'
import ErrorBoundary from '../ErrorBoundary'
import RenderChild from './RenderChild'

interface TextProps {
  as?: React.ElementType
  size?: number | string
  linkify?: boolean
  children?: React.ReactNode
  truncate?: boolean
  fontSize?: string
  ellipsis?: boolean
  className?: string
  translate?: boolean
  fontWeight?: number
  translateProps?: Record<string, string>
  highlightText?: string
  whatsappFormat?: boolean
}

export const Text = (props: TextProps) => {
  const {
    as: Component = 'span',
    size = 512,
    linkify,
    children,
    truncate,
    fontSize = 'base',
    ellipsis,
    className,
    translate,
    fontWeight = 400,
    translateProps,
    highlightText,
    whatsappFormat,
    ...rest
  } = props

  if (!props.children) {
    return null
  }

  const textClass = classNames(styles.textBase, {
    [styles[fontSize]]: fontSize,
    [styles[`weight-${fontWeight}`]]: fontWeight,
    [className || '']: className,
    [styles.ellipsis]: ellipsis,
  })

  return (
    <ErrorBoundary
      componentName="Text"
      errorUI={
        <span data-testid="auto-id-6e6cbdc3-18d4-4dc7-9761-7501abbead2e">
          !
        </span>
      }
    >
      <Component className={textClass} {...rest}>
        <RenderChild
          translate={translate || false}
          translateProps={translateProps || {}}
          truncate={truncate || false}
          size={typeof size === 'string' ? parseInt(size, 10) : size}
          highlightText={highlightText || ''}
          fontSize={fontSize}
          linkify={linkify || false}
          whatsappFormat={whatsappFormat || false}
        >
          {children}
        </RenderChild>
      </Component>
    </ErrorBoundary>
  )
}

Text.fontSize = {
  p8: 'xxs',
  p11: 'xss',
  p12: 'xs',
  p14: 'base',
  p16: 'sm',
  p18: 'lg',
  p20: 'xl',
  p24: 'xl2',
  p30: 'xl3',
  p36: 'xl4',
  p40: 'xl5',
  p48: 'xl6',
}

Text.propTypes = {
  as: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  linkify: PropTypes.bool,
  children: PropTypes.node,
  truncate: PropTypes.bool,
  fontSize: PropTypes.string,
  ellipsis: PropTypes.bool,
  className: PropTypes.string,
  translate: PropTypes.bool,
  fontWeight: PropTypes.oneOf([300, 400, 500, 600, 700]),
  highlightText: PropTypes.string,
  translateProps: PropTypes.any,
  title: PropTypes.string,
  onClick: PropTypes.func,
  id: PropTypes.string,
  style: PropTypes.object,
  whatsappFormat: PropTypes.bool,
}
